
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Copy, 
  Scissors, 
  Clipboard, 
  ArrowUpDown,
  Droplets, 
  PaintBucket, 
  Palette, 
  Move, 
  ImagePlus, 
  Save
} from 'lucide-react';
import { BannerSize } from '@/types/banner';
import BannerPreview from '@/components/admin/marketing/BannerPreview';

interface BannerEditorStepProps {
  selectedSize?: BannerSize;
  selectedTemplateId?: string;
  onFinish: () => void;
}

const BannerEditorStep: React.FC<BannerEditorStepProps> = ({
  selectedSize = 'medium_rectangle',
  selectedTemplateId,
  onFinish
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [bannerSettings, setBannerSettings] = useState({
    backgroundColor: '#ffffff',
    imageUrl: selectedTemplateId ? `/templates/${selectedTemplateId}.png` : undefined,
    imageSettings: {
      width: 100,
      opacity: 100,
      position: { x: 50, y: 50 },
      isAnimated: false
    },
    text: 'Your Banner Text',
    textColor: '#000000',
    fontSize: 24
  });
  
  // Mock function to update banner properties
  const updateBannerProperty = (property: string, value: any) => {
    setBannerSettings(prev => ({
      ...prev,
      [property]: value
    }));
  };
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Left Sidebar - Editor Tools */}
      <div className="lg:col-span-3">
        <div className="bg-white p-4 rounded-lg shadow space-y-6">
          <div>
            <h3 className="font-medium mb-2">Editor Tools</h3>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Basic</TabsTrigger>
                <TabsTrigger value="advanced">Advanced</TabsTrigger>
                <TabsTrigger value="text">Text</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Clipboard</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <Button variant="outline" size="sm">
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                    <Button variant="outline" size="sm">
                      <Scissors className="h-4 w-4 mr-1" />
                      Cut
                    </Button>
                    <Button variant="outline" size="sm">
                      <Clipboard className="h-4 w-4 mr-1" />
                      Paste
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Transform</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      <ArrowUpDown className="h-4 w-4 mr-1" />
                      Resize
                    </Button>
                    <Button variant="outline" size="sm">
                      <Droplets className="h-4 w-4 mr-1" />
                      Effects
                    </Button>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="advanced" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Fill & Color</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      <PaintBucket className="h-4 w-4 mr-1" />
                      Fill
                    </Button>
                    <Button variant="outline" size="sm">
                      <Palette className="h-4 w-4 mr-1" />
                      Color
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Position</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      <Move className="h-4 w-4 mr-1" />
                      Move
                    </Button>
                    <Button variant="outline" size="sm">
                      <ArrowUpDown className="h-4 w-4 mr-1" />
                      Align
                    </Button>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="text" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Text Content</h4>
                  <textarea 
                    className="w-full p-2 border rounded"
                    rows={3}
                    value={bannerSettings.text}
                    onChange={(e) => updateBannerProperty('text', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Text Color</h4>
                  <input 
                    type="color"
                    value={bannerSettings.textColor}
                    onChange={(e) => updateBannerProperty('textColor', e.target.value)} 
                    className="w-full p-1 border rounded h-10"
                  />
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Font Size</h4>
                  <input 
                    type="range" 
                    min="12" 
                    max="48" 
                    step="1"
                    value={bannerSettings.fontSize}
                    onChange={(e) => updateBannerProperty('fontSize', parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-center">{bannerSettings.fontSize}px</div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
          
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Banner Background</h4>
            <input 
              type="color"
              value={bannerSettings.backgroundColor}
              onChange={(e) => updateBannerProperty('backgroundColor', e.target.value)} 
              className="w-full p-1 border rounded h-10"
            />
          </div>
          
          <div className="space-y-2 pt-4">
            <Button variant="outline" className="w-full mb-2" size="sm">
              <ImagePlus className="h-4 w-4 mr-2" />
              Add Image
            </Button>
            <Button onClick={onFinish} className="w-full">
              <Save className="h-4 w-4 mr-2" />
              Save Banner
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main Content - Banner Preview */}
      <div className="lg:col-span-9">
        <div className="bg-gray-100 p-6 rounded-lg shadow-inner min-h-[500px] flex flex-col">
          <h2 className="text-lg font-medium mb-4">Preview: {selectedSize.replace('_', ' ')}</h2>
          
          <div className="flex-grow flex items-center justify-center">
            <BannerPreview
              size={selectedSize}
              backgroundColor={bannerSettings.backgroundColor}
              imageUrl={bannerSettings.imageUrl}
              imageSettings={bannerSettings.imageSettings}
              text={bannerSettings.text}
              textColor={bannerSettings.textColor}
              // Removed the fontSize prop since it doesn't exist in BannerPreviewProps
              // Instead, we could add a textLine with this font size if needed
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerEditorStep;
